import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Star, ExternalLink, CheckCircle, XCircle, Clock, Eye, Image as ImageIcon } from 'lucide-react';

interface ThirdPartySubmission {
  id: string;
  practitioner_id: string;
  platform_name: string;
  order_count: number | null;
  review_count: number | null;
  rating: number | null;
  supporting_documents: string[];
  screenshot_urls: string[];
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  practitioners?: {
    first_name: string | null;
    last_name: string | null;
    professional_title: string | null;
  };
}

export function ThirdPartyRatingReview() {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<ThirdPartySubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('third_party_rating_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load submissions'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = async (submissionId: string, status: 'approved' | 'rejected') => {
    try {
      setReviewingId(submissionId);
      const submission = submissions.find(s => s.id === submissionId);
      if (!submission) return;

      // Update submission status
      const { error: updateError } = await supabase
        .from('third_party_rating_submissions')
        .update({
          status,
          admin_notes: adminNotes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', submissionId);

      if (updateError) throw updateError;

      toast({
        title: `Submission ${status}`,
        description: `The third-party rating submission has been ${status}`
      });

      // Reset form and refresh
      setAdminNotes('');
      fetchSubmissions();
    } catch (error) {
      console.error('Error reviewing submission:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update submission'
      });
    } finally {
      setReviewingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const viewScreenshot = async (filePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('practitioner_files')
        .createSignedUrl(filePath, 60);

      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing screenshot:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load screenshot'
      });
    }
  };

  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
  const reviewedSubmissions = submissions.filter(s => s.status !== 'pending');

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="text-lg">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary">
          {pendingSubmissions.length} pending review
        </Badge>
      </div>

      {/* Pending Reviews */}
      {pendingSubmissions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending Reviews ({pendingSubmissions.length})
            </CardTitle>
            <CardDescription>
              Submissions waiting for admin approval
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingSubmissions.map((submission) => (
              <div key={submission.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-lg">
                      Practitioner ID: {submission.practitioner_id}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Platform: <strong>{submission.platform_name}</strong>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Submitted {new Date(submission.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge className={getStatusColor(submission.status)}>
                    {getStatusIcon(submission.status)}
                    <span className="ml-1">{submission.status}</span>
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 p-3 bg-muted rounded">
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {submission.rating ? submission.rating.toFixed(1) : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {submission.order_count ?? 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Orders</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {submission.review_count ?? 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Reviews</div>
                  </div>
                </div>

                {/* Screenshots */}
                {submission.screenshot_urls && submission.screenshot_urls.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium flex items-center gap-1">
                      <ImageIcon className="h-4 w-4" />
                      Screenshots ({submission.screenshot_urls.length}):
                    </span>
                    <div className="flex gap-2 flex-wrap">
                      {submission.screenshot_urls.map((url, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          onClick={() => viewScreenshot(url)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View {index + 1}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {submission.supporting_documents && submission.supporting_documents.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2">Supporting Documents:</h5>
                    <div className="space-y-1">
                      {submission.supporting_documents.map((doc, index) => (
                        <a
                          key={index}
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Document {index + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Admin Notes (Optional)</label>
                    <Textarea
                      placeholder="Add notes about this review..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReview(submission.id, 'approved')}
                      disabled={reviewingId === submission.id}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {reviewingId === submission.id ? 'Processing...' : 'Approve'}
                    </Button>
                    <Button
                      onClick={() => handleReview(submission.id, 'rejected')}
                      disabled={reviewingId === submission.id}
                      variant="destructive"
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {reviewingId === submission.id ? 'Processing...' : 'Reject'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Reviewed Submissions */}
      {reviewedSubmissions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Review History ({reviewedSubmissions.length})
            </CardTitle>
            <CardDescription>
              Previously reviewed submissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {reviewedSubmissions.map((submission) => (
              <div key={submission.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium">
                      Practitioner ID: {submission.practitioner_id}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {submission.platform_name} • Reviewed {new Date(submission.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={getStatusColor(submission.status)}>
                    {getStatusIcon(submission.status)}
                    <span className="ml-1">{submission.status}</span>
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-muted-foreground">Rating:</span>
                    <span className="ml-2 font-medium">
                      {submission.rating ? submission.rating.toFixed(1) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Orders:</span>
                    <span className="ml-2 font-medium">
                      {submission.order_count ?? 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Reviews:</span>
                    <span className="ml-2 font-medium">
                      {submission.review_count ?? 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Screenshots */}
                {submission.screenshot_urls && submission.screenshot_urls.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm font-medium flex items-center gap-1 mb-2">
                      <ImageIcon className="h-4 w-4" />
                      Screenshots ({submission.screenshot_urls.length}):
                    </span>
                    <div className="flex gap-2 flex-wrap">
                      {submission.screenshot_urls.map((url, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          onClick={() => viewScreenshot(url)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View {index + 1}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {submission.admin_notes && (
                  <div className="mt-3 p-3 bg-muted rounded">
                    <p className="text-sm">
                      <strong>Admin Notes:</strong> {submission.admin_notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {submissions.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No third-party rating submissions found</p>
        </div>
      )}
    </div>
  );
}